import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Calendar, ChevronDown, ChevronLeft, ChevronRight, DollarSign, Edit2, FileText,
  Loader2, MessageSquare, Repeat, ShieldOff, Tag,
} from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../../lib/api.js';
import useQuickSale from '../QuickSaleModal.jsx';

const isSold = (d) => d?.systemCode === 'SOLD' || d?.slug === 'sold' || /sold/i.test(d?.label || d?.name || '');

/**
 * Purple "Lead actions" dropdown for the dialer's View Lead panel. Every action
 * is a plain API call, so using it never touches the live call.
 * @param {Object} props
 * @param {string} props.leadId
 * @param {object|null} props.lead
 * @param {() => void} props.onEdit - Open the edit modal.
 * @param {() => void} props.onNotes - Scroll to / focus the notes box.
 * @param {() => void} props.onSms - Open the pre-set SMS dialog.
 * @param {() => void} props.onChanged - Refresh caches after any change.
 * @returns {JSX.Element}
 */
export default function LeadActionsMenu({ leadId, lead, onEdit, onNotes, onSms, onChanged }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('root'); // 'root' | 'dispositions' | 'drips'
  const [busy, setBusy] = useState(null);
  const ref = useRef(null);
  const { askSale, saleModal } = useQuickSale();

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setView('root'); } };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const { data: dispData } = useQuery({
    queryKey: ['dispositions'],
    queryFn: () => apiGet('/dispositions'),
    enabled: open,
    staleTime: 60_000,
  });
  const dispositions = (dispData?.dispositions || []).filter((d) => !d.archived && d.isActive !== false);

  const { data: dripData } = useQuery({
    queryKey: ['marketing-drips'],
    queryFn: () => apiGet('/marketing/drips'),
    enabled: open && view === 'drips',
    staleTime: 60_000,
  });
  const drips = (Array.isArray(dripData) ? dripData : dripData?.drips ?? [])
    .map((d) => ({ id: d.dripGroupId || d._id, name: d.name }));

  function close() { setOpen(false); setView('root'); }

  /** Run one action: show a spinner on its row, toast the outcome, refresh. */
  async function run(key, fn, okMsg) {
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      onChanged?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.message || 'Action failed');
    } finally {
      setBusy(null);
    }
  }

  async function applyDisposition(d) {
    let sale = null;
    if (isSold(d)) {
      close();
      sale = await askSale();
      if (sale == null) return; // cancelled: nothing changes
    }
    await run(
      d._id,
      () => apiPost('/dispositions/apply', { leadId, dispositionId: d._id, ...(sale || {}) }),
      `Disposition set: ${d.label || d.name}`,
    );
  }

  async function markSold() {
    close();
    const sale = await askSale();
    if (sale == null) return;
    await run('sold', () => apiPost(`/leads/${leadId}/disposition`, { status: 'SOLD', ...sale }), 'Marked as sold');
  }

  const item = 'w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-civix-purple/15 flex items-center gap-2 disabled:opacity-50';
  const Row = ({ icon: Icon, label, onClick, arrow, busyKey, danger }) => (
    <button type="button" className={`${item} ${danger ? 'text-red-300' : ''}`} onClick={onClick} disabled={busy === busyKey && !!busyKey}>
      {busy === busyKey && busyKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4 text-civix-purpleSoft" />}
      <span className="flex-1">{label}</span>
      {arrow && <ChevronRight className="w-4 h-4 text-white/40" />}
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      {saleModal}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="bg-civix-purple text-white hover:bg-civix-purpleDeep rounded-lg px-3 py-1.5 flex items-center gap-1.5 shadow-glow-purple text-xs font-bold"
      >
        <Tag className="w-4 h-4" /> LEAD ACTIONS <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div role="menu" className="absolute left-0 top-10 w-64 max-h-[60vh] overflow-y-auto z-50 rounded-xl bg-civix-panel border border-civix-border shadow-xl py-1">
          {view === 'root' && (
            <>
              <Row icon={Edit2} label="Edit Lead Details" onClick={() => { close(); onEdit(); }} />
              <Row icon={FileText} label="Add Notes" onClick={() => { close(); onNotes(); }} />
              <Row icon={Tag} label="Assign Disposition Tag" arrow onClick={() => setView('dispositions')} />
              <div className="h-px bg-civix-border my-1" />
              <Row icon={MessageSquare} label="Send Pre-set SMS" onClick={() => { close(); onSms(); }} />
              <Row icon={Repeat} label="Enroll in Drip Campaign" arrow onClick={() => setView('drips')} />
              <Row icon={Calendar} label="Schedule Appointment / Callback" busyKey="followup"
                onClick={() => run('followup', () => apiPost(`/leads/${leadId}/disposition`, { status: 'FOLLOW_UP' }), 'Follow-up scheduled')} />
              <Row icon={DollarSign} label="Mark as Sold" onClick={markSold} />
              <Row icon={ShieldOff} label={lead?.blocked ? 'Unblock' : 'Block'} busyKey="block" danger
                onClick={() => run('block', () => apiPut(`/leads/${leadId}`, { blocked: !lead?.blocked }), lead?.blocked ? 'Lead unblocked' : 'Lead blocked')} />
            </>
          )}

          {view !== 'root' && (
            <>
              <button type="button" className={`${item} text-white/50`} onClick={() => setView('root')}>
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              {view === 'dispositions' && (dispositions.length === 0
                ? <div className="px-3 py-2 text-xs text-white/40">No dispositions yet.</div>
                : dispositions.map((d) => (
                  <button key={d._id} type="button" className={item} onClick={() => applyDisposition(d)} disabled={busy === d._id}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color || '#7C3AED' }} />
                    <span className="flex-1">{d.label || d.name}</span>
                    {busy === d._id && <Loader2 className="w-4 h-4 animate-spin" />}
                  </button>
                )))}
              {view === 'drips' && (drips.length === 0
                ? <div className="px-3 py-2 text-xs text-white/40">No drip campaigns found.</div>
                : drips.map((d) => (
                  <button key={d.id} type="button" className={item} disabled={busy === d.id}
                    onClick={() => run(d.id, async () => {
                      const r = await apiPost('/leads/bulk/enroll-drip', { leadIds: [leadId], dripGroupId: d.id });
                      if (!(r?.enrolled > 0)) throw new Error(r?.alreadyEnrolled > 0 ? 'Already enrolled in this drip' : 'Could not enroll lead');
                    }, 'Lead added to drip')}>
                    <span className="flex-1">{d.name}</span>
                    {busy === d.id && <Loader2 className="w-4 h-4 animate-spin" />}
                  </button>
                )))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
